import {Avatar} from 'platform/components';
import {
  Box,
  HStack,
  Show,
  Text,
  type ThemeColorPath,
  type ThemeIconKey,
  VStack,
} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import type {CustomerInsightsRecord} from '../../../types/CustomerInsightsViewModel';
import {CustomerInsightsClickableRow} from './CustomerInsightsClickableRow';
import {CustomerInsightsDateMetadata} from './CustomerInsightsDateMetadata';
import {CustomerInsightsHistoryBusinessCasePrice} from './CustomerInsightsHistoryBusinessCasePrice';
import {CustomerInsightsLink} from './CustomerInsightsLink';

export interface CustomerInsightsHistoryAppearance {
  background: ThemeColorPath;
  color: ThemeColorPath;
  icon: ThemeIconKey;
}

const getHistoryRecordIcon = (
  record: CustomerInsightsRecord,
  historyKind: CustomerInsightsHistoryRecordProps['historyKind'],
  appearance: CustomerInsightsHistoryAppearance
): ThemeIconKey =>
  match({historyKind, recordKind: record.kind})
    .returnType<ThemeIconKey>()
    .with({historyKind: 'lost', recordKind: 'interest'}, () => 'sidebar/interest')
    .with({historyKind: 'lost', recordKind: 'businessCase'}, () => 'sidebar/business_case')
    .otherwise(() => appearance.icon);

interface CustomerInsightsHistoryRecordProps {
  record: CustomerInsightsRecord;
  historyKind: 'sales' | 'purchases' | 'lost' | 'service';
  appearance: CustomerInsightsHistoryAppearance;
  hasConnector: boolean;
}

const renderHistoryLink = (record: CustomerInsightsRecord, value: string, isTitle = false) =>
  match(record.href)
    .with(Pattern.string, (href) => (
      <CustomerInsightsLink
        href={href}
        title={value}
        size={isTitle ? 'small' : 'xSmall'}
        overflowWrap="anywhere"
      />
    ))
    .otherwise(() => (
      <Text
        size={isTitle ? 'small' : 'xSmall'}
        alternative={isTitle}
        color={isTitle ? 'primary' : 'secondary'}
        noWrap
      >
        {value}
      </Text>
    ));

const renderHistoryRecordPrice = (record: CustomerInsightsRecord) =>
  match(record)
    .with({kind: 'businessCase'}, (businessCaseRecord) => (
      <CustomerInsightsHistoryBusinessCasePrice record={businessCaseRecord} />
    ))
    .otherwise(() => null);

export function CustomerInsightsHistoryRecord(props: CustomerInsightsHistoryRecordProps) {
  return (
    <CustomerInsightsClickableRow href={props.record.href}>
      <Box paddingHorizontal={4} paddingVertical={1} role="listitem" minWidth={0}>
        <HStack spacing={2} align="stretch" minWidth={0}>
          <VStack align="center">
            <Avatar
              size="small"
              variant="square"
              icon={getHistoryRecordIcon(props.record, props.historyKind, props.appearance)}
              color={props.appearance.background}
              iconColor={props.appearance.color}
            />
            <Show when={props.hasConnector}>
              <Box flex={1} minHeight={2} borderLeft="thin solid" borderColor="general.separator" />
            </Show>
          </VStack>
          <VStack spacing={1} minWidth={0} grow={1}>
            {renderHistoryLink(props.record, props.record.title, true)}
            <Show when={props.record.owner}>
              <Text size="xSmall" color="secondary" noWrap>
                {props.record.owner}
              </Text>
            </Show>
          </VStack>
          <Show when={props.record.kind === 'businessCase' || props.record.occurredAt}>
            <VStack spacing={0} align="flex-end">
              {renderHistoryRecordPrice(props.record)}
              <Show when={props.record.occurredAt}>
                <CustomerInsightsDateMetadata
                  occurredAt={props.record.occurredAt ?? ''}
                  data-testid={`customerInsights-history-${props.record.id}`}
                />
              </Show>
            </VStack>
          </Show>
        </HStack>
      </Box>
    </CustomerInsightsClickableRow>
  );
}
